import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useGetAppealsQuery } from "../store/api";
import { useNavigate } from "react-router-dom";
import { Appeal } from "../types/api";

const AppealsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  background: var(--bg-card);
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--border-divider);
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  background: var(--bg-card);
  font-size: 0.875rem;
  color: var(--text-primary);
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #b2b2b2;
    box-shadow: 0 0 0 3px rgba(178, 178, 178, 0.1);
  }
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #b2b2b2;
    box-shadow: 0 0 0 3px rgba(178, 178, 178, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const AppealsCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-divider);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: var(--bg-primary);
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-divider);
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background: var(--bg-primary);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-light);
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  ${({ status }) => {
    switch (status) {
      case "PENDING":
        return "background: var(--status-pending-bg); color: var(--status-pending-text);";
      case "IN_PROGRESS":
        return "background: var(--status-processing-bg); color: var(--status-processing-text);";
      case "APPROVED":
        return "background: var(--status-success-bg); color: var(--status-success-text);";
      case "REJECTED":
        return "background: var(--status-error-bg); color: var(--status-error-text);";
      case "MERCHANT_RESP_PENDING":
        return "background: #ffedd5; color: #9a3412;";
      case "SUPPORT_REVIEW":
        return "background: #e0e7ff; color: #3730a3;";
      case "TRADER_REVIEW":
        return "background: #fef9c3; color: #854d0e;";
      default:
        return "background: var(--bg-secondary); color: var(--text-primary);";
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

const HelperText = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

// Компонент таймера обратного отсчёта
function CountdownTimer({ deadline }: { deadline: string }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();
      const dl = new Date(deadline).getTime();
      const diff = dl - now;
      if (diff <= 0) {
        setTimeLeft("Время истекло");
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  const isExpired = timeLeft === "Время истекло";
  return (
    <span style={{
      fontWeight: 600,
      fontSize: '0.8125rem',
      fontFamily: 'monospace',
      color: isExpired ? '#dc2626' : '#f59e0b',
      whiteSpace: 'nowrap',
    }}>
      {timeLeft}
    </span>
  );
}

function Appeals() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("");
  const [page] = useState(1);

  // Получаем реальные данные апелляций
  const { data: appealsResponse, isLoading } = useGetAppealsQuery({
    page,
    limit: 20,
    status: statusFilter === "all" ? undefined : statusFilter,
    orderId: orderFilter || undefined,
  });

  const appeals: Appeal[] = appealsResponse?.data || [];
  const totalAppeals: number = appealsResponse?.meta?.total || 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Ожидает";
      case "IN_PROGRESS":
        return "В работе";
      case "APPROVED":
        return "Одобрена";
      case "REJECTED":
        return "Отклонена";
      case "SUPPORT_REVIEW":
        return "На рассмотрении";
      case "MERCHANT_RESP_PENDING":
        return "Ваш ответ";
      case "TRADER_REVIEW":
        return "У трейдера";
      case "SECONDARY_DISPUTE":
        return "Вторичная";
      default:
        return status;
    }
  };

  const handleAppealClick = (appealId: string) => {
    navigate(`/appeals/${appealId}`);
  };

  const filteredAppeals = appeals.filter((appeal) => {
    if (statusFilter !== "all" && appeal.status !== statusFilter) return false;
    if (orderFilter && !appeal.order?.invId?.includes(orderFilter))
      return false;
    return true;
  });

  return (
    <AppealsContainer>
      <Header>
        <div>
          <HelperText>
            Управление вашими апелляциями по ордерам. Всего: {totalAppeals}
          </HelperText>
        </div>
      </Header>

      <FilterBar>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Все статусы</option>
          <option value="PENDING">Ожидает</option>
          <option value="MERCHANT_RESP_PENDING">Ожидает вашего ответа</option>
          <option value="SUPPORT_REVIEW">На рассмотрении саппорта</option>
          <option value="TRADER_REVIEW">На рассмотрении трейдера</option>
          <option value="IN_PROGRESS">В работе</option>
          <option value="APPROVED">Одобрена</option>
          <option value="REJECTED">Отклонена</option>
        </Select>

        <Input
          type="text"
          placeholder="Поиск по ID ордера"
          value={orderFilter}
          onChange={(e) => setOrderFilter(e.target.value)}
        />
      </FilterBar>

      <AppealsCard>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>ID Апелляции</TableHeaderCell>
              <TableHeaderCell>ID Ордера</TableHeaderCell>
              <TableHeaderCell>Реквизит</TableHeaderCell>
              <TableHeaderCell>Сумма (местная валюта)</TableHeaderCell>
              <TableHeaderCell>Сумма в USDT</TableHeaderCell>
              <TableHeaderCell>Курс</TableHeaderCell>
              <TableHeaderCell>Создано</TableHeaderCell>
              <TableHeaderCell>Причина</TableHeaderCell>
              <TableHeaderCell>Статус</TableHeaderCell>
              <TableHeaderCell></TableHeaderCell>
              <TableHeaderCell>Файлы</TableHeaderCell>
              <TableHeaderCell>Подробнее</TableHeaderCell>
            </tr>
          </TableHeader>
          <tbody>
            {isLoading ? (
              <tr>
                <TableCell colSpan={12}>
                  <EmptyState>Загрузка...</EmptyState>
                </TableCell>
              </tr>
            ) : filteredAppeals.length === 0 ? (
              <tr>
                <TableCell colSpan={12}>
                  <EmptyState>
                    {appeals.length === 0
                      ? "У вас пока нет апелляций"
                      : "Нет результатов по фильтрам"}
                  </EmptyState>
                </TableCell>
              </tr>
            ) : (
              filteredAppeals.map((appeal: Appeal) => (
                <TableRow
                  key={appeal.id}
                  onClick={() => handleAppealClick(appeal.id)}
                >
                  <TableCell>#{appeal.id.substring(0, 8)}</TableCell>
                  <TableCell>{appeal.order?.invId || appeal.orderId}</TableCell>
                  <TableCell>
                    {appeal.order?.cardNumber ||
                      appeal.order?.phoneNumber ||
                      appeal.order?.accountNumber ||
                      "—"}
                  </TableCell>
                  <TableCell>
                    {appeal.order?.amount
                      ? `${appeal.order.amount.toFixed(2)} ₽`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {(appeal.order as any)?.usdtAmount
                      ? `${(appeal.order as any).usdtAmount.toFixed(2)} USDT`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {(appeal.order as any)?.exchangeRate
                      ? (appeal.order as any).exchangeRate.toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell>{formatDate(appeal.createdAt)}</TableCell>
                  <TableCell>{appeal.reason || "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={appeal.status}>
                      {getStatusText(appeal.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {appeal.status === "MERCHANT_RESP_PENDING" && appeal.deadline ? (
                      <CountdownTimer deadline={appeal.deadline} />
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const parseLen = (v: any) => {
                        if (!v) return 0;
                        if (Array.isArray(v)) return v.length;
                        if (typeof v === 'string') {
                          try { const arr = JSON.parse(v); return Array.isArray(arr) ? arr.length : 0; } catch { return 0; }
                        }
                        return 0;
                      };
                      const total = parseLen(appeal.images) + parseLen(appeal.videos) + parseLen(appeal.documents);
                      return total > 0 ? `${total} файл(ов)` : "—";
                    })()}
                  </TableCell>
                  <TableCell>
                    <button
                      style={{
                        background: "none",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.25rem",
                        padding: "0.25rem 0.5rem",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                        color: "var(--text-primary)",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAppealClick(appeal.id);
                      }}
                    >
                      Открыть
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>
      </AppealsCard>
    </AppealsContainer>
  );
}

export default Appeals;
